<template>
  <div class="w-full">
    <div class="flex items-center gap-4">
      <!-- Left label: At start -->
      <div class="shrink-0 text-right">
        <div class="leading-4 font-medium flex items-baseline gap-1 justify-end">
          <span v-tippy="tooltipStart">{{ formattedStart }}</span>
          <span class="text-sm text-quaternary">{{ unit }}</span>
        </div>
        <div class="text-xs leading-4 text-quaternary">At start</div>
      </div>

      <!-- Bar area -->
      <div class="relative flex-1 py-10">
        <!-- Current label positioned at end of gradient (current) -->
        <div
          class="absolute -top-1 left-0 flex flex-col items-center"
          :style="{ left: currentPercent + '%', transform: 'translateX(-50%)' }"
        >
          <div class="leading-4 font-semibold flex items-baseline gap-1">
            <span v-tippy="tooltipCurrent">{{ formattedCurrent }}</span>
            <span class="text-sm text-quaternary">{{ unit }}</span>
          </div>
          <div class="text-xs leading-4 text-quaternary">Current</div>
          <div
            class="mt-[2px] w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent"
            style="border-top-color: var(--border-brand)"
          />
        </div>

        <!-- Expected label below marker -->
        <div
          class="absolute bottom-0 left-0 translate-y-[22px] text-center"
          :style="{ left: expectedLeftCss, transform: 'translate(-50%, 0)' }"
        >
          <div class="leading-4 font-medium flex items-baseline gap-1 justify-center">
            <span v-tippy="tooltipExpected">{{ formattedExpected }}</span>
            <span class="text-sm text-quaternary">{{ unit }}</span>
          </div>
          <div class="text-xs leading-4 text-quaternary">Expected</div>
        </div>

        <!-- Bar container -->
        <div class="relative h-8 overflow-hidden">
          <!-- Filled gradient for current -->
          <div
            class="absolute left-0 top-0 h-full rounded opacity-60 z-0"
            :style="{
              width: currentPercent + '%',
              background: 'linear-gradient(90deg, var(--brand-500), var(--pink-500))'
            }"
          ></div>

          <!-- Hatched remaining area from current to end -->
          <div
            class="absolute top-0 right-0 h-full rounded z-0"
            :style="{
              // Start hatched region 4px after current for visual separation
              left: 'calc(' + currentPercent + '% + 4px)',
              background:
                'repeating-linear-gradient(45deg, transparent, transparent 6px, var(--bg-quaternary) 6px, var(--bg-quaternary) 12px)'
            }"
          ></div>

          <!-- Dotted expected marker line -->
          <div
            class="absolute top-[-4px] bottom-[-10px] border-l-2 border-dotted z-10 border-brand"
            :style="{ left: expectedLeftCss }"
          ></div>
        </div>
      </div>

      <!-- Right label: At end -->
      <div class="shrink-0 text-left">
        <div class="leading-4 font-medium flex items-baseline gap-1">
          <span v-tippy="tooltipEnd">{{ formattedEnd }}</span>
          <span class="text-sm text-quaternary">{{ unit }}</span>
        </div>
        <div class="text-xs leading-4 text-quaternary">At end</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

// stores
// components
// composables
// utilities
import { formatAbbreviatedNumber, formatNumber } from '@/utilities/number'

/**
 * Input data for the mileage tracker.
 */
interface MileageData {
  /** Starting mileage value. */
  atStart: number
  /** Current mileage value. */
  current: number
  /** Expected mileage value (forecast). */
  expected: number
  /** Ending mileage value (allowance end). */
  atEnd: number
  /** Units for display: 'mi' or 'km'. */
  unit: 'mi' | 'km'
}

const props = defineProps<{
  /** Data used to render the tracker. */
  mileageData: MileageData
}>()

/**
 * Clamp a number between 0 and 100.
 */
function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value))
}

/**
 * Format a value using the project's number formatter.
 */
function formatFull(value: number): string {
  try {
    const formatted = formatNumber(value, 'normal', 0)
    return formatted == null ? String(value) : String(formatted)
  } catch (error) {
    console.error('Failed to format number', error)
    return String(value)
  }
}

/**
 * Format a compact K-suffixed value (e.g., 29000 -> 29K).
 */
function formatK(value: number): string {
  try {
    if (value >= 1000) {
      const condensed = formatAbbreviatedNumber(Number(value), 2, false)
      return String(condensed).toUpperCase()
    }
    const formatted = formatNumber(value, 'normal', 0)
    return formatted == null ? String(value) : String(formatted)
  } catch (error) {
    console.error('Failed to format compact number', error)
    return String(value)
  }
}

/**
 * Percentage positions for current and expected relative to the end value.
 */
const currentPercent = computed<number>(() => {
  try {
    if (!props.mileageData?.atEnd || props.mileageData.atEnd <= 0) return 0
    const pct = (props.mileageData.current / props.mileageData.atEnd) * 100
    return clampPercent(Math.round(pct))
  } catch (error) {
    console.error('Failed to compute current percent', error)
    return 0
  }
})

const expectedPercent = computed<number>(() => {
  try {
    if (!props.mileageData?.atEnd || props.mileageData.atEnd <= 0) return 0
    const pct = (props.mileageData.expected / props.mileageData.atEnd) * 100
    return clampPercent(Math.round(pct))
  } catch (error) {
    console.error('Failed to compute expected percent', error)
    return 0
  }
})

/**
 * CSS left value for expected marker that clamps within the bar area.
 */
const expectedLeftCss = computed<string>(() => {
  try {
    const clamped = clampPercent(expectedPercent.value)
    return `${clamped}%`
  } catch (error) {
    console.error('Failed to compute expected left css', error)
    return '0%'
  }
})

/**
 * Formatted labels matching the screenshot.
 */
const formattedStart = computed<string>(() => formatK(props.mileageData.atStart))
const formattedEnd = computed<string>(() => formatK(props.mileageData.atEnd))
const formattedCurrent = computed<string>(() => formatK(props.mileageData.current))
const formattedExpected = computed<string>(() => formatK(props.mileageData.expected))

const unit = computed<string>(() => props.mileageData.unit || 'mi')

/** Tooltip contents with full, non-compacted values */
const tooltipStart = computed(() => ({
  content: `${formatFull(props.mileageData.atStart)} ${unit.value}`
}))
const tooltipCurrent = computed(() => ({
  content: `${formatFull(props.mileageData.current)} ${unit.value}`
}))
const tooltipExpected = computed(() => ({
  content: `${formatFull(props.mileageData.expected)} ${unit.value}`
}))
const tooltipEnd = computed(() => ({
  content: `${formatFull(props.mileageData.atEnd)} ${unit.value}`
}))
</script>

<style scoped>
/* No extra styles; layout achieved with utility classes and inline styles */
</style>
